import React from 'react'
import propTypes from 'prop-types'

import { BarChart } from '@components/charts/BarChart'
import { Card, CardHeading, CardContent, CardFooter } from '@components/layout/Card'
import { LineChart } from '@components/charts/LineChart'
import { WordCloudChart } from '@components/charts/WordCloudChart'

import { Stack } from '@UILibrary/Stack'
import { Text } from '@UILibrary/Text'

import { StarRating } from '../Shared/StarRating'
import { LearnMore } from '../Shared/LearnMore'
import { DataPlaceholder } from '../Shared/DataPlaceholder'
import { FeedbackTable } from '../Shared/FeedbackTable'

import { buildLineChartPropData, buildBarChartDataProp, buildWordCloudDataProp, buildWorkshopAttendanceChartDataProp } from './charts/data'
import { buildFunnelBarChartOptionsProp, buildWorkshopAttendanceChartOptionsProp } from './charts/options'
import { buildBarChartPluginsProp } from './charts/plugins'

import styles from './engagement-feedback.module.sass'

export const EngagementFeedback = (props) => {
  const { engagementFeedback, organizationName } = props
  const feedbackHeading = organizationName ? `${organizationName}'s Feedback` : "Organization Feedback"

  return (
    <div>
      <Text heading2b as="h1" colour="default" margin={{ 'bottom': 'l' }}>Your team's engagement with the programme</Text>

      <Stack horizontal spacing="m">
        <Card>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>1:1 coaching</Text>
            { engagementFeedback.coaching_feedback && (
              <Text body as="p">
                <Text bodylarge as="strong" colour="secondary-main">{`${engagementFeedback.coaching_feedback.percentage_variance}%`}</Text> of enrolled team members have had their coaching sessions
              </Text>
            )}
          </CardHeading>
          <CardContent>
            <BarChart
              height='small'
              data={buildBarChartDataProp(engagementFeedback.coaching_attendance_breakdown)}
              options={buildFunnelBarChartOptionsProp(engagementFeedback.coaching_attendance_breakdown)}
              plugins={buildBarChartPluginsProp()}
            />
          </CardContent>
          <CardFooter>
            <LearnMore buttonText="How is coaching engagement tracked?" heading="Coaching Engagement Breakdown" subHeading="">
              <Text body as="p">
                Tracking coaching engagement happens through our booking engine.
              </Text>
              <Text body as="p">
                Once we enroll members into the program, and the first workshop has kicked off, we'll start our outreach campaign.
              </Text>
              <Text body as="p">
                First, we'll attempt to contact each member via multiple channels to set up their coaching session. Their sessions will be booked, and then finally, once they've received coaching, the system is also updated.
              </Text>
              <Text body as="p">
                We calculate totals and drop off at each stage, and the final calculation is the number of people who have had sessions with their coach divided by the number of people enrolled.
              </Text>
              <Text body as="p">
                This gives us the engagement rate for the program.
              </Text>
              <Text body as="p">
                We look at this score on a per cohort basis, and then calculate a population average.
              </Text>
            </LearnMore>
          </CardFooter>
        </Card>

        <Card>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Workshop attendance</Text>
            {props.engagementFeedback.workshop_attendance_breakdown && (
              <Text body as="p">
                <Text bodylarge as="strong" colour="secondary-main">{`${engagementFeedback.workshop_attendance_breakdown.percentage_variance}%`}</Text> enrolled members have attended a workshop sessions
              </Text>
            )}
          </CardHeading>
          <CardContent>
            { engagementFeedback.workshop_attendance_breakdown ? (
              <BarChart
                height='small'
                options={buildWorkshopAttendanceChartOptionsProp()}
                data={buildWorkshopAttendanceChartDataProp(engagementFeedback.workshop_attendance_breakdown)}
              />
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
        </Card>
      </Stack>

      <Stack horizontal spacing="m">
        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Average workshop rating</Text>
          </CardHeading>
          <CardContent>
            {engagementFeedback.workshop_feedback ? (
              <div>
                <Text title1b as="h1" colour="secondary-main" textAlign="center">{engagementFeedback.workshop_feedback.average_rating}</Text>
                <StarRating rating={engagementFeedback.workshop_feedback.average_rating} />
                <Text body as="p" textAlign="center">{engagementFeedback.workshop_feedback.feedback_count} ratings</Text>
              </div>
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
        </Card>

        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Average coaching rating</Text>
          </CardHeading>
          <CardContent>
            {engagementFeedback.coaching_feedback ? (
              <div>
                <Text title1b as="h1" colour="secondary-main" textAlign="center">{engagementFeedback.coaching_feedback.average_rating}</Text>
                <StarRating rating={engagementFeedback.coaching_feedback.average_rating} />
                <Text body as="p" textAlign="center">{engagementFeedback.coaching_feedback.feedback_count} ratings</Text>
              </div>
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
        </Card>

        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Average app rating</Text>
          </CardHeading>
          <CardContent>
            {engagementFeedback.app_feedback ? (
              <div>
                <Text title1b as="h1" colour="secondary-main" textAlign="center" margin={{ 'top': 'm' }}>{engagementFeedback.app_feedback.average_rating}</Text>
                <StarRating rating={engagementFeedback.app_feedback.average_rating} />
                <Text body as="p" textAlign="center">{engagementFeedback.app_feedback.feedback_count} ratings</Text>
              </div>
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardHeading>
          <Text heading4a colour="secondary-main" as="h3" margin='none'>What your team thinks about the programme</Text>
          {engagementFeedback.program_feedback && (
            <Text as="p" margin={{ bottom: 's' }}>
              <Text
                heading3
                as="strong"
                colour="secondary-main"
                margin={{ right: 'xs' }}
              >
              {engagementFeedback.program_feedback.positive_feedback_percentage}%
            </Text>
              of all feedback is positive
            </Text>
          ) }
        </CardHeading>
        <CardContent>
          {engagementFeedback.program_feedback && engagementFeedback.program_feedback.all_feedback.length > 0 ? (
            <Stack horizontal spacing="m">
              <div className={styles['program-feedback-text']}>
                <Text
                  title1a
                  as="h1"
                  textAlign="center"
                >
                  "{engagementFeedback.program_feedback.sample_feedback?.length > 52 ?
                    `${engagementFeedback.program_feedback.sample_feedback.substring(0, 52)}...` :
                    engagementFeedback.program_feedback.sample_feedback}"
                </Text>
              </div>
              <div className={styles['program-feedback-wordcloud']}>
                <WordCloudChart
                  height='medium'
                  words={buildWordCloudDataProp(engagementFeedback.program_feedback.wordcloud)}
                />
              </div>
            </Stack>
          ) : (
            <DataPlaceholder />
          )}
        </CardContent>
        {engagementFeedback.program_feedback && engagementFeedback.program_feedback.all_feedback.length > 0 ? (
          <CardFooter>
            <LearnMore buttonText="See All Feedback" heading={feedbackHeading} subHeading="">
              <FeedbackTable
                data={engagementFeedback.program_feedback.all_feedback}
                showAreaColumn={true}
              />
            </LearnMore>
          </CardFooter>
        ) : null}
      </Card>

      <Stack horizontal spacing="m" >
        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Workshop feedback</Text>
          </CardHeading>
          <CardContent>
            {engagementFeedback.workshop_feedback && engagementFeedback.workshop_feedback.feedback.length > 0 ? (
              <WordCloudChart
                height='small'
                words={buildWordCloudDataProp(engagementFeedback.workshop_feedback.feedback)}
              />
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
          {engagementFeedback.workshop_feedback && engagementFeedback.workshop_feedback.all_feedback.length > 0 && (
            <CardFooter>
              <LearnMore buttonText="See All Feedback" heading={feedbackHeading} subHeading="">
                <FeedbackTable
                  data={engagementFeedback.workshop_feedback.all_feedback}
                  showAreaColumn={false}
                />
              </LearnMore>
            </CardFooter>
          )}
        </Card>

        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Coaching feedback</Text>
          </CardHeading>
          <CardContent>
            {engagementFeedback.coaching_feedback && engagementFeedback.coaching_feedback.feedback.length > 0 ? (
              <WordCloudChart
                height='small'
                words={buildWordCloudDataProp(engagementFeedback.coaching_feedback.feedback)}
              />
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
          {engagementFeedback.coaching_feedback && engagementFeedback.coaching_feedback.all_feedback.length > 0 && (
            <CardFooter>
              <LearnMore buttonText="See All Feedback" heading={feedbackHeading} subHeading="">
                <FeedbackTable
                  data={engagementFeedback.coaching_feedback.all_feedback}
                  showAreaColumn={false}
                />
              </LearnMore>
            </CardFooter>
          )}
        </Card>

        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>App feedback</Text>
          </CardHeading>
          <CardContent>
            {engagementFeedback.app_feedback && engagementFeedback.app_feedback.length > 0 ? (
              <WordCloudChart
                height='small'
                words={buildWordCloudDataProp(engagementFeedback.app_feedback.feedback)}
              />
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
          {engagementFeedback.app_feedback && engagementFeedback.app_feedback.all_feedback.length > 0 ? (
            <CardFooter>
              <LearnMore buttonText="See All Feedback" heading={feedbackHeading} subHeading="">
                <FeedbackTable
                  data={engagementFeedback.app_feedback.all_feedback}
                  showAreaColumn={false}
                />
              </LearnMore>
            </CardFooter>
          ) : null}
        </Card>
      </Stack>

      <Card>
        <CardHeading>
          <Text heading4a colour="secondary-main" as="h3" margin='none'>Deep-dive: into areas of the programme</Text>
          <Text body as="p" margin='none'>You can see the number of employees enrolled and how many have received coaching over time.</Text>
        </CardHeading>
        <CardContent>
          <LineChart
            height='medium'
            labels={engagementFeedback.enrollment_breakdown.labels}
            datasets={buildLineChartPropData(engagementFeedback.enrollment_breakdown.datasets)}
          />
          <Text bodySmall as="p" textAlign='center' margin='none'>
            (click legends to toggle on and off)
          </Text>
        </CardContent>
      </Card>
    </div>
  )
}

EngagementFeedback.propTypes = {
  engagementFeedback: propTypes.object.isRequired,
  organizationName: propTypes.string,
}
